import { HelpOutline } from '@mui/icons-material';
import { Typography } from '@mui/material';
import Tippy from '@tippyjs/react';
import { FormInput, Row, Spacer } from '.';
import { FormInputProps } from './FormInput';
import { useRef, type JSX } from 'react';

interface HorizontalFormInputProps<T> extends FormInputProps<T> {
  label?: string;
  labelStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  isDomain?: boolean;
  formattedValue?: string;
  rightAlignedKey?: boolean;
  extraStyles?: React.CSSProperties;
  helpText?: JSX.Element;
  textSize?: 'small' | 'title';
  autoFocus?: boolean;
}

const HorizontalFormInput = <T,>(props: HorizontalFormInputProps<T>) => {
  const inputRef = useRef<HTMLElement>(null);
  const { label, readonly, initialValue, formattedValue, helpText, textSize } = props;
  const isDomain = props.isDomain ?? false;
  const rowStyles: React.CSSProperties = props.rightAlignedKey
    ? { flexWrap: 'wrap', gap: '20px', alignItems: 'center', ...props.extraStyles }
    : { alignItems: 'center', gap: '20px', ...props.extraStyles };

  const labelStyles = props.rightAlignedKey
    ? { flex: '2 0 10px', justifyContent: 'flex-end', ...props.labelStyle }
    : { ...props.labelStyle };

  const inputStyles = props.rightAlignedKey
    ? { flex: '2 0 10px', ...props.inputStyle }
    : { ...props.inputStyle };

  return (
    <Row style={{ ...rowStyles }}>
      {label && (
        <Row style={{ ...labelStyles }}>
          <Typography
            variant={
              textSize && textSize === 'small'
                ? 'p14'
                : textSize === 'title'
                  ? 'p18SemiBold'
                  : 'body1'
            }
            color={textSize === 'title' ? 'secondary.active' : 'info.main'}
            sx={{ marginBottom: textSize === 'title' ? 0.5 : 0 }}>
            {label}
          </Typography>
          {helpText && (
            <>
              <Spacer width='xxxs' />
              <Tippy placement='right' content={helpText} theme='tippy-rounded'>
                <HelpOutline />
              </Tippy>
            </>
          )}
        </Row>
      )}

      {readonly ? (
        <>
          {isDomain && (
            <>
              <Typography variant='body1' color='secondary.dark'>
                @
              </Typography>
              <Spacer width='xxxs' />
            </>
          )}
          <Typography style={{ ...inputStyles }} color='secondary.dark'>
            {formattedValue ? formattedValue : initialValue}
          </Typography>
        </>
      ) : (
        <>
          {isDomain && (
            <>
              <Typography variant='body1' color='secondary.dark'>
                @
              </Typography>
              <Spacer width='xxxs' />
            </>
          )}
          <FormInput inputRef={inputRef} {...props} inputStyle={{ ...inputStyles }} />
        </>
      )}
    </Row>
  );
};

export default HorizontalFormInput;
